import { EyeOutlined } from '@ant-design/icons';
import PermissionCheck from 'common/features/permission-check';
import ActionButtons from 'common/navigation/components/action-buttons';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose } from 'redux';
import styled from 'styled-components';
import TicketButton from 'tickets/ticket-button';
import { Button, Icon, notification } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'utils/cops-moment';
import { getValueAndPostfix } from 'utils/helpers';
import { withRouter } from 'utils/withRouter';
import { referenceTypes } from '../../../utils/app-configs';
import { animateScrollTo, isObjectNullOrEmpty } from '../../../utils/helpers';
import Spinner from '../../spinner/components/spinner';
import TimelineTable from './timeline-table';
import TimelineToolbar from './timeline-toolbar';

const TimelineContainer = styled.div`
  .redux-infinite-scroll {
    overflow: auto !important;
    margin-bottom: 0 !important;
  }
  ul {
    list-style: none;
    padding-left: 0;
  }
`;

const HeaderTitle = styled.span`
  ${p => p.bold && 'font-weight: 700'};
`;

const ShowMoreButton = styled(Button)`
  font-size: 1rem;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const ShowMoreText = styled.div`
  font-size: 1rem;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const StyledIcon = styled(Icon)`
  vertical-align: sub;
  color: ${p =>
    p.highPrio ? p.theme.colors.pendingSmartActionColor.highPrio :
      p.theme.colors.pendingSmartActionColor.lowPrio};
`;

let scrolledIntoView = false;

const Timeline = ({
  intl,
  reload,
  data,
  loadMorePast,
  pastItemsAvailable,
  isLoadingPast,
  loadMoreFuture,
  futureItemsAvailable,
  isLoading,
  canCreateTicket,
  canCreateOrder,
  renderMenu,
  renderConnectionStatus,
  isInactive,
  id,
  history,
  type = referenceTypes.equipment,
  jumpToDate,
  activeContext,
  ...rest
}) => {
  const elements = useRef({});
  const [datesToRerender, setDatesToRerender] = useState([]);
  const [availableActions, setAvailableActions] = useState({});
  const selectedRowIds = useRef({});
  const setSelectedIds = (ids) => {
    selectedRowIds.current = ids;
  };

  useEffect(() => () => {
    scrolledIntoView = false;
  }, []);

  useDeepCompareEffect(() => {
    if (jumpToDate && !isObjectNullOrEmpty(data) && data[jumpToDate]) {
      const el = elements.current[jumpToDate];
      if (el && !scrolledIntoView) {
        animateScrollTo(el, 120);
        scrolledIntoView = true;
      }
    }
  }, [jumpToDate, data]);

  const getIdLabel = (objectType) => {
    if (objectType === 'order' || type === 'historicalorder') {
      return intl.formatMessage({ id: 'timeline.ORDER_ID_COL' });
    }
    if (objectType === 'notification') {
      return intl.formatMessage({ id: 'timeline.NOTIF_ID_COL' });
    }
    if (objectType === 'call') {
      return intl.formatMessage({ id: 'timeline.CALL_ID_COL' });
    }
    return '';
  };

  const getFormattedNumber = (number, numberOfDigits) => {
    const { value, postfix } = getValueAndPostfix(number);
    return `${intl.formatNumber(value, {
      minimumFractionDigits: postfix ? 1 : numberOfDigits,
      maximumFractionDigits: postfix ? 1 : numberOfDigits,
    })}${postfix}`;
  };

  const getHeaderTitle = (date, specialDate, specialDateName, toDate, translate) => {
    const momentDate = moment(date, 'YYYY-MM-DD');
    let title;
    if (specialDate) {
      title = `${translate({
        id: `timeline.${specialDateName}`,
      })}: ${momentDate.format('LL')}`;
    } else {
      title = momentDate.format('LL');
    }
    return <HeaderTitle bold={momentDate.isSame(toDate, 'day')}>{title}</HeaderTitle>;
  };

  const getColumns = () => [
    {
      renderLabelFn: (value, record) => getIdLabel(record.type),
      dataIndex: 'description',
      format: 'sapId',
      width: '140px',
    },
    {
      label: intl.formatMessage({ id: 'timeline.DESCRIPTION_COL' }),
      dataIndex: 'title',
    },
    {
      label: intl.formatMessage({ id: 'timeline.TYPE_COL' }),
      dataIndex: ['data', 'businessIndicator'],
      width: '50px',
    },
    {
      label: intl.formatMessage({ id: 'timeline.STATUS_COL' }),
      renderValueFn: (value, record) =>
        record.type === 'order' ?
          <FormattedMessage id={`timeline.table.status.${record.data.status}`} />
          :
          `${record.data.status}`,
      width: '170px',
    },
    {
      renderLabelFn: (value, record) =>
        record?.parentType?.toLowerCase() === referenceTypes.equipment ?
          intl.formatMessage({ id: 'timeline.EQUIPMENT_COL' }) :
          intl.formatMessage({ id: 'timeline.FUNCTIONAL_LOCATION_NUMBER' }),
      format: 'sapId',
      renderValueFn: (value, record) => {
        if (record?.parentType?.toLowerCase() === referenceTypes.equipment) {
          return record?.data?.equipmentSerialNumber;
        }
        return record?.data?.functionalLocationNumber;
      },
      width: '130px',
    },
    {
      renderLabelFn: (value, record) => record.type === 'order' &&
        intl.formatMessage({ id: 'timeline.COST_COL' }),
      renderValueFn: (value, record) =>
        // eslint-disable-next-line max-len
        record.type === 'order' && `${getFormattedNumber(record.data.actualCost || 0, 0)} / ${getFormattedNumber(
          record.data.plannedCost || 0,
          0,
        )}`,
      width: '120px',
    },
    {
      label: intl.formatMessage({ id: 'timeline.LABOR_COL' }),
      renderValueFn: (value, record) =>
        `${getFormattedNumber(record.data.actualLabor || 0, 1)} / ${getFormattedNumber(
          record.data.plannedLabor || 0,
          1,
        )}`,
      width: '120px',
    },
    {
      label: intl.formatMessage({ id: 'timeline.WORKCENTER_COL' }),
      dataIndex: ['data', 'technicianName'],
      width: '200px',
    },
    {
      label: intl.formatMessage({ id: 'timeline.PARENT_TYPE' }),
      dataIndex: 'parentType',
      width: '190px',
    },
    {
      renderValueFn: (value, record) =>
        record?.indicators?.ticketAction ?
          <StyledIcon
            highPrio={record?.indicators?.ticketAction === 'smartaction_highprio'}
            className='fa-wand-magic-sparkles'
            size='sm'
          />
          : null,
      width: '20px',
    },
  ];

  const reloadTimeline = () => {
    setAvailableActions({});
    setSelectedIds({});
    reload();
  };

  const loadPast = () => {
    if (data && Object.keys(data).length > 1 && pastItemsAvailable && !isLoadingPast) {
      loadMorePast();
    }
  };

  // The timeline handles row select differently as worklist.
  // Timeline rowkeys are kept across all tables (dates).
  // In worklist, or any normal table, it's only kept for that one table.
  const handleRowSelect = (ids, key) => {
    const selectedIds = selectedRowIds.current || {};

    let newSelectedIds = { ...selectedIds, [key]: ids };

    let allSelectedIds = Object.keys(newSelectedIds).reduce((all, i) =>
      [...all, ...newSelectedIds[i]], []);

    let allRows = Object.keys(data).reduce((all, i) => [...all, ...data[i].data], []);
    let selectedRows = allRows?.filter(r => allSelectedIds.includes(r.id));

    // If the selectedRows consist of orders & notifications/calls,
    // show warning notification.
    if (
      selectedRows.some(r => r.type === 'order') &&
      selectedRows.some(r => r.type === 'call' || r.type === 'notification')
    ) {
      notification.warning({
        message: intl.formatMessage({
          id: 'timeline.toolbar.MSG_PROCESSED_TOGETHER',
        }),
        placement: 'top',
      });
      newSelectedIds = selectedIds;
      allSelectedIds = Object.keys(newSelectedIds).reduce((all, i) =>
        [...all, ...newSelectedIds[i]], []);

      allRows = Object.keys(data).reduce((all, i) => [...all, ...data[i].data], []);
      selectedRows = allRows?.filter(r => allSelectedIds.includes(r.id));
    }

    const allActions = selectedRows.reduce((actions, row) => [...actions, ...row.actions], []);

    // Create a object with all available actions.
    // key: actionId, value: array of payload to pass
    const newAvailableActions = allActions.reduce((actions, action) => {
      // All rows selected should have the action available, or the button will dissapear
      if (selectedRows.every(row => row.actions?.find(r => r.id === action.id))) {
        return {
          ...actions,
          [action.id]: actions[action.id]
            ? [...actions[action.id], action.payload]
            : [action.payload],
        };
      }
      return actions;
    }, {});

    const newDatesToRerender = [
      ...datesToRerender,
      ...selectedRows?.map(r => moment(r.date).format('YYYY-MM-DD')),
    ];
    const uniqueDatesToRerended = [...new Set(newDatesToRerender)];

    setSelectedIds(newSelectedIds);
    setAvailableActions(newAvailableActions);
    setDatesToRerender(uniqueDatesToRerended);
  };

  const renderDateblock = () => {
    if (data && Object.keys(data).length) {
      const items = Object.keys(data)?.map((key) => {
        const tableData = data[key].data;
        //  Make sure all items are unique
        // Temp solution until rewrite of timeline
        const tableItems = [
          ...new Set(tableData?.map(obj => obj.id)),
        ]?.map(s => tableData?.find(obj => obj.id === s));
        const tableconf = {
          headerTitle: getHeaderTitle(
            key,
            data[key].specialDate,
            data[key].specialDateName,
            jumpToDate,
            intl.formatMessage,
          ),
          columns: getColumns(),
          data: tableItems?.sort((a, b) => a.id.localeCompare(b.id)),
          selectedRowKeys: [],
          onRowClick: item =>
            item.uri &&
            history.push(`/${item.uri}/dashboard`, {
              context: item.id,
              technician: type === 'technician' ? id : null,
            }),
        };

        return (
          <TimelineTable
            key={key}
            rowKey='id'
            date={key}
            tableconf={tableconf}
            innerRef={el => (elements.current[key] = el)}
            specialDate={data[key].specialDate}
            onRowSelect={ids => handleRowSelect(ids, key)}
            selectedRowKeys={selectedRowIds?.current[key] || []}
            datesToRerender={datesToRerender}
            pageSize={data[key].data.length}
            activeRow={activeContext && { id: activeContext, rowField: 'id' }}
          />
        );
      });
      return items;
    }
    return [];
  };

  return (
    <>
      {renderMenu(
        <ActionButtons>
          {renderConnectionStatus && renderConnectionStatus()}
          {isInactive || !canCreateOrder ?
            null :
            <PermissionCheck permissionsList={['ordercreate']}>
              <Button
                data-test-id='create-ad-hoc-order-button'
                type='secondary'
                onClick={() => history.push(`create-order/${type}/${id}`)}
              >
                <FormattedMessage id='common.CREATE_ORDER' />
              </Button>
            </PermissionCheck>
          }
          {canCreateTicket ? <TicketButton id={id} /> : null}
        </ActionButtons>,
      )}
      <TimelineContainer data-test-id='timeline'>
        <>
          <TimelineToolbar
            availableActions={availableActions}
            reloadTimeline={reloadTimeline}
            {...rest}
          />
          {!isLoading && futureItemsAvailable &&
            <ShowMoreButton icon={<EyeOutlined />} onClick={loadMoreFuture}>
              <FormattedMessage id='timeline.SHOW_MORE_BTN' />
            </ShowMoreButton>
          }
          {!isLoading && !futureItemsAvailable &&
            <ShowMoreText>
              <FormattedMessage id='timeline.NO_ITEMS_MSG' />
            </ShowMoreText>
          }
          {isLoading && <Spinner title={intl.formatMessage({ id: 'timeline.LOADING_MSG' })} />}
        </>
        <div>
          {!isLoading &&
            <InfiniteScroll
              element='ul'
              loadMore={loadPast}
              hasMore={pastItemsAvailable}
              loader={<Spinner key={0} />}
              initialLoad={false}
            >
              {renderDateblock()}
            </InfiniteScroll>
          }
        </div>
      </TimelineContainer>
    </>
  );
};

Timeline.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  type: PropTypes.string,
  loadMoreFuture: PropTypes.func.isRequired,
  loadMorePast: PropTypes.func.isRequired,
  reload: PropTypes.func.isRequired,
  jumpToDate: PropTypes.string,
  futureItemsAvailable: PropTypes.bool.isRequired,
  pastItemsAvailable: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  history: PropTypes.object,
  intl: PropTypes.object,
  activeContext: PropTypes.string,
  renderMenu: PropTypes.func,
  renderConnectionStatus: PropTypes.func,
  canCreateOrder: PropTypes.bool,
  canCreateTicket: PropTypes.bool,
  isInactive: PropTypes.bool,
  isLoadingPast: PropTypes.bool,
};

export default compose(
  injectIntl,
  withRouter,
)(Timeline);
